// The Cards view's card size (#6785, prototype toolbarHTML + mxAll, `:2576-2631`): the
// "Card size" control above the grid sets every card to Full or Simplified, and a card's
// own chevron overrides that for the one card. Default: Simplified (the prototype's Fleet
// page starts its cards simplified, `:3315`).
//
// A PER-VIEWER PREFERENCE, NEVER SHARED STATE (v2 quirk 4). Both choices ride ONE
// `localStorage` key through `./local-pref` (try/catch, a zod schema, the default on any
// failure), so a private window or a corrupt value reads as the default and never throws.
// Per page, never a global store: `<FleetMachines/>` makes one.

import { createSignal } from 'solid-js';
import { z } from 'zod';
import { readLocalPref, writeLocalPref } from './local-pref';

export const CARD_SIZES = ['full', 'simplified'] as const;
export type CardSize = (typeof CARD_SIZES)[number];

export const CARD_SIZE_KEY = 'dz-fleet-card-size';

/** What the key holds: the grid's choice, and each card the chevron moved off it. */
export type CardSizePref = {
  readonly all: CardSize;
  readonly cards: Readonly<Record<string, CardSize>>;
};

export const DEFAULT_CARD_SIZE: CardSizePref = { all: 'simplified', cards: {} };

const CardSizeSchema = z.object({
  all: z.enum(CARD_SIZES),
  cards: z.record(z.string(), z.enum(CARD_SIZES)),
});

export type CardSizes = {
  /** The grid control's choice. */
  readonly all: () => CardSize;
  /** Is this card simplified: its own chevron's choice, else the grid's. */
  readonly simplified: (id: string) => boolean;
  /** The grid control: every card to `size`, dropping each card's own choice. */
  readonly setAll: (size: CardSize) => void;
  /** A card's chevron. */
  readonly setCard: (id: string, simplified: boolean) => void;
  /**
   * Forget the choices of machines no longer in the fleet (`ids`, the unfiltered read), so
   * the key does not grow with every machine ever removed. Writes only when one goes.
   */
  readonly prune: (ids: readonly string[]) => void;
};

export function createCardSizes(): CardSizes {
  const [pref, setPref] = createSignal<CardSizePref>(
    readLocalPref(CARD_SIZE_KEY, CardSizeSchema, DEFAULT_CARD_SIZE),
  );
  const save = (next: CardSizePref): void => {
    setPref(next);
    writeLocalPref(CARD_SIZE_KEY, next);
  };
  return {
    all: () => pref().all,
    simplified: (id) => (pref().cards[id] ?? pref().all) === 'simplified',
    setAll: (size) => save({ all: size, cards: {} }),
    setCard: (id, simplified) => {
      const size: CardSize = simplified ? 'simplified' : 'full';
      const { [id]: _, ...rest } = pref().cards;
      save({ all: pref().all, cards: size === pref().all ? rest : { ...rest, [id]: size } });
    },
    prune: (ids) => {
      const live = new Set(ids);
      const cards = pref().cards;
      const kept = Object.fromEntries(Object.entries(cards).filter(([id]) => live.has(id)));
      if (Object.keys(kept).length !== Object.keys(cards).length) {
        save({ all: pref().all, cards: kept });
      }
    },
  };
}
